import { Navigate, NavLink, Route, Routes } from "react-router"
import * as React from "react"
import ApplicationLayout from "@rio-cloud/rio-uikit/ApplicationLayout"
import NotificationsContainer from "@rio-cloud/rio-uikit/NotificationsContainer"
import ApplicationHeader from "@rio-cloud/rio-uikit/ApplicationHeader"
import ActionBarItem from "@rio-cloud/rio-uikit/ActionBarItem"
import { routingConfig } from "./RoutingConfig"
import { FormattedMessage, IntlProvider, useIntl } from "react-intl"
import { DefaultUserMenu } from "@rio-cloud/rio-user-menu-component"
import IframeResizer from "iframe-resizer-react"
import { AgreementDialog } from "../Agreement/AgreementDialog"
import { WelcomePage } from "../WelcomePage/WelcomePage"
import { TransportOrderFrame } from "../TransportOrder/TransportOrderFrame"
import { OrderOverview } from "../TransportOrder/OrderOverview"
import { ClosingDaysFrame } from "../ClosingDays/ClosingDaysFrame"
import { NotificationSettings } from "../Notifications/NotificationSettings"
import { getLanguageSettings } from "@/redux/lang.selector"
import { useAppSelector } from "@/redux/store"
import { config } from "@/configuration/app/app-config"
import { useState } from "react"
import OnboardingTip from "@rio-cloud/rio-uikit/OnboardingTip"
import Dialog from "@rio-cloud/rio-uikit/Dialog"
import { Faq } from "../WelcomePage/Faq"
import Button from "@rio-cloud/rio-uikit/Button"
import { ContactForm } from "../WelcomePage/ContactForm"
import { CookiesProvider } from "react-cookie"
import { TransportExecutionFrame } from "../TransportExecution/TransportExecutionFrame"
import { subjectSelector } from "@/configuration/login/login.selector"
import { AdminApplication } from "@/components/RioAdmin/AdminApplication"
import { useFeatureToggle } from "@/configuration/featuretoggle/hooks"
import { InboundTransportOrders } from "@/components/InboundTransportOrder/InboundTransportOrders"

const menu = <IframeResizer className="iFrameResizer" src={"https://menu.rio.cloud"}/>

const ClosingDaysNavLink = (): React.ReactElement => {
    const [showOnboardingTip, setShowOnboardingTip] = useState(true)

    const showClosingDaysOnboardingTipUntil = "2024-12-01"
    const localStorageDontShowAgainProperty = `onboardingTip_closingDays_dontShowAgain`

    const badgeNewFeature = (
        <span className={"badge badge-secondary margin-left-5 text-uppercase"}>
            <FormattedMessage id={"navbar.badge.new"}/>
        </span>
    )
    const adminNavLink = <NavLink
        data-track-ga-event-trigger="click"
        data-track-ga-event-category="dispatch, navigationMenu, closingDays"
        data-track-ga-event-action="Link"
        to={`${routingConfig.closingDays.path}`}
    >
        <FormattedMessage id={"navbar.navigationitem.closingdays"}/>
        {new Date() < new Date(showClosingDaysOnboardingTipUntil) ? badgeNewFeature : null}
    </NavLink>

    const shouldShowOnboardingTip = new Date() < new Date(showClosingDaysOnboardingTipUntil)
    const dontShowAgain = localStorage.getItem(localStorageDontShowAgainProperty) === "true"
    if (!shouldShowOnboardingTip || dontShowAgain) {
        return adminNavLink
    }

    const handleDontShowAgain = () => {
        setShowOnboardingTip(false)
        localStorage.setItem(localStorageDontShowAgainProperty, "true")
    }

    return (
        <OnboardingTip
            show={showOnboardingTip}
            placement="bottom"
            width={500}
            textAlignment="left"
            title={<FormattedMessage id={"navbar.navigationitem.closingdays.onboardingtip.title"}/>}
            content={
                <div>
                    <p className={"white-space-pre-line"}>
                        <FormattedMessage id={"navbar.navigationitem.closingdays.onboardingtip.body"}/>
                    </p>
                    <Button className="btn btn-secondary hover border-none" onClick={handleDontShowAgain} iconName="rioglyph-eye-close">
                        <FormattedMessage id={"navbar.navigationitem.closingdays.onboardingtip.dontshowagain"}/>
                    </Button>
                </div>
            }
            onHide={() => {
                setShowOnboardingTip(false)
            }}
        >
            {adminNavLink}
        </OnboardingTip>
    )
}

interface ServiceInfoMenuItemProps {
    handleOnClickFaq: () => void
    handleOnClickContact: (openOnboarding: boolean) => void
}

const ServiceInfoMenuItem: React.FunctionComponent<ServiceInfoMenuItemProps> = ({
    handleOnClickFaq,
    handleOnClickContact
}: ServiceInfoMenuItemProps) => {
    const intl = useIntl()

    return (
        <ActionBarItem id="serviceInfo">
            <ActionBarItem.Icon>
                <span className="icon rioglyph rioglyph-info-sign"/>
            </ActionBarItem.Icon>
            <ActionBarItem.Popover title={<FormattedMessage id={"application.name"}/>} className={"divider-y"}>
                <ActionBarItem.List>
                    <ActionBarItem.ListItem icon='rioglyph-question-sign' onClick={handleOnClickFaq}>
                        <FormattedMessage id={"application.actionBar.serviceInfo.faq.text"}/>
                    </ActionBarItem.ListItem>
                    <ActionBarItem.ListItem icon='rioglyph-envelope' onClick={() => handleOnClickContact(false)}>
                        <FormattedMessage id={"application.actionBar.serviceInfo.contact.text"}/>
                    </ActionBarItem.ListItem>
                    <ActionBarItem.ListSeparator/>
                    <ActionBarItem.ListItem>

                        <div>
                            <div className="line-height-largest">
                                <a href={intl.formatMessage({ id: "termsAndConditions.agreement.dialog.checkTermsAndConditionsLabel.linkTermsConditions.href" })}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-track-ga-event-trigger="click"
                                    data-track-ga-event-category="dispatch, transportOrder, serviceInfo"
                                    data-track-ga-event-action="termsAndConditions">
                                    <FormattedMessage
                                        id={"application.actionBar.serviceInfo.termsAndConditions.text"}/>
                                </a>
                            </div>
                            <div className="line-height-largest">
                                <FormattedMessage id={"application.actionBar.serviceInfo.version.text"}
                                    values={{ version: config.release.revision?.substring(0, 10) }}/>
                            </div>
                            <div className="line-height-largest">
                                <FormattedMessage id={"application.actionBar.serviceInfo.buildTime.text"}
                                    values={{ buildTime: config.release.builtAt }}/>
                            </div>
                        </div>
                    </ActionBarItem.ListItem>
                </ActionBarItem.List>

            </ActionBarItem.Popover>
        </ActionBarItem>)
}

const welcomePageNavItem = {
    key: "welcomePage",
    route: <NavLink
        data-track-ga-event-trigger="click"
        data-track-ga-event-category="dispatch, navigationMenu, welcomePage"
        data-track-ga-event-action="Link"
        to={"/welcome-page"}
    >
        <FormattedMessage id={"navbar.navigationitem.welcome"}/>
    </NavLink>,
}

const transportOrderNavItem = {
    key: "transportorder",
    route: <NavLink
        data-track-ga-event-trigger="click"
        data-track-ga-event-category="dispatch, navigationMenu, transportOrders"
        data-track-ga-event-action="Link"
        to={`${routingConfig.landingPage.path}`}
    >
        <FormattedMessage id={"navbar.navigationitem.dashboard"}/>
    </NavLink>,
}

const inboundTransportOrderNavItem = {
    key: "inboundTransportOrders",
    route: <NavLink
        to={routingConfig.inboundTransportOrderOverview.path}
    >
        <FormattedMessage id="navbar.navigationitem.inboundTransportOrders"/>
    </NavLink>,
}

const notificationsNavItem = {
    key: "notifications",
    route: <NavLink
        data-track-ga-event-trigger="click"
        data-track-ga-event-category="dispatch, navigationMenu, notifications"
        data-track-ga-event-action="Link"
        to={`${routingConfig.notificationSettings.path}`}
    >
        <FormattedMessage id={"navbar.navigationitem.notifications"}/>
    </NavLink>,
}

const closingDaysNavItem = {
    key: "closingDays",
    route: <ClosingDaysNavLink/>,
}

const transportExecutionNavItem = {
    key: "transportExecution",
    route: <NavLink
        data-track-ga-event-trigger="click"
        data-track-ga-event-category="dispatch, navigationMenu, transportExecution"
        data-track-ga-event-action="Link"
        to={`${routingConfig.transportExecution.path}`}
    >
        <FormattedMessage id={"navbar.navigationitem.shipments"}/>
    </NavLink>,
}

const UserApplication = () => {
    const inboundTransportOrdersFeature = useFeatureToggle("showInboundTransportOrders", false)
    const navItems = [
        welcomePageNavItem,
        transportOrderNavItem,
        ...(inboundTransportOrdersFeature ? [inboundTransportOrderNavItem] : []),
        notificationsNavItem,
        closingDaysNavItem,
        transportExecutionNavItem,
    ]

    const defaultUserMenu = (
        <DefaultUserMenu environment={config.mode} key="userMenu"/>
    )

    const [showFaqDialog, setShowFaqDialog] = useState(false)
    const [showContactDialog, setShowContactDialog] = useState(false)

    const showFaqHandler = () => {
        setShowFaqDialog(true)
    }

    const showContactHandler = () => {
        setShowContactDialog(true)
    }

    return (
        <div>
            <Dialog
                show={showFaqDialog}
                title={<FormattedMessage id={"application.actionBar.serviceInfo.faq.text"}/>}
                body={<Faq/>}
                bsSize={Dialog.SIZE_XL}
                footer={<Button onClick={() => setShowFaqDialog(false)}
                    variant={Button.VARIANT_OUTLINE}><FormattedMessage
                        id={"application.close"}/></Button>}
                onClose={() => setShowFaqDialog(false)}
                className='faqDialog'
                showCloseButton
            />
            <Dialog
                show={showContactDialog}
                title={<FormattedMessage id={"application.actionBar.serviceInfo.contact.text"}/>}
                body={<ContactForm />}
                bsSize={Dialog.SIZE_XL}
                footer={<Button onClick={() => setShowContactDialog(false)}
                    variant={Button.VARIANT_OUTLINE}><FormattedMessage
                        id={"application.close"}/></Button>}
                onClose={() => setShowContactDialog(false)}
                className='contactDialog'
                showCloseButton
            />
            <AgreementDialog/>
            <ApplicationLayout className={"printable-content"}>
                <ApplicationLayout.Header>
                    <ApplicationHeader
                        appNavigator={menu}
                        label={<FormattedMessage id={"application.name"}/>}
                        navItems={navItems}
                        actionBarItems={[<ServiceInfoMenuItem handleOnClickFaq={showFaqHandler}
                            handleOnClickContact={showContactHandler}
                            key="serviceInfo"/>, defaultUserMenu]}
                    />

                </ApplicationLayout.Header>
                <Routes>
                    <Route path="/" index element={<Navigate to="/welcome-page"/>}/>
                    <Route path="/welcome-page" element={<WelcomePage handleOnClickFaq={showFaqHandler}
                        handleOnClickContact={showContactHandler}/>}/>
                    <Route path={routingConfig.transportOrderPresenter.path}
                        element={<TransportOrderFrame/>}/>
                    <Route path={routingConfig.transportOrderOverview.path} element={<OrderOverview/>}/>
                    <Route
                        path={routingConfig.inboundTransportOrderOverview.path}
                        element={inboundTransportOrdersFeature
                            ? <InboundTransportOrders/>
                            : <Navigate to={routingConfig.transportOrderOverview.path} replace/>
                        }
                    />
                    <Route path={routingConfig.closingDays.path} element={<ClosingDaysFrame/>}/>
                    <Route path={routingConfig.transportExecution.path} element={<TransportExecutionFrame/>}/>
                    <Route path={routingConfig.notificationSettings.path} element={<NotificationSettings/>}/>
                    <Route path={"*"}
                        element={<Navigate to={routingConfig.transportOrderOverview.path} replace/>}/>
                </Routes>
            </ApplicationLayout>
        </div>
    )
}

export const Application = () => {
    const languageSettings = useAppSelector(getLanguageSettings)
    const userSubject = useAppSelector(subjectSelector)
    const displayRioAdminTransportOrders = userSubject?.startsWith("azure-my-rio-de")

    return (
        <CookiesProvider>
            <IntlProvider locale={languageSettings.locale} messages={languageSettings.messages}>
                <Routes>
                    {displayRioAdminTransportOrders &&
                        <Route path={routingConfig.rioAdminTransportOrders.path} element={<AdminApplication/>}/>}
                    <Route path="/*" element={<UserApplication/>}/>
                </Routes>
                <NotificationsContainer/>
            </IntlProvider>
        </CookiesProvider>
    )
}
